import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { portfolio } from '../mock';

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState('all');

  const allProjects = [...portfolio.web, ...portfolio.mobile];
  const filteredProjects =
    activeTab === 'all'
      ? allProjects
      : activeTab === 'web'
      ? portfolio.web
      : portfolio.mobile;

  return (
    <section id="portfolio" className="py-24 relative overflow-hidden bg-white">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Title */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xs font-bold tracking-widest uppercase text-indigo-600 mb-3"
          >
            Featured Works
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-5xl font-extrabold font-display text-slate-900 tracking-tight mb-8"
          >
            3D & Mobile App Showcase
          </motion.p>

          {/* Filter Tabs */}
          <div className="inline-flex p-1.5 rounded-full glass border border-slate-200 space-x-2 shadow-sm">
            {[
              { id: 'all', label: 'All Projects' },
              { id: 'web', label: 'Web Applications' },
              { id: 'mobile', label: 'Mobile Apps' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Portfolio Cards Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <AnimatePresence>
            {filteredProjects.map((item) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                key={item.id}
                className="group relative rounded-2xl overflow-hidden glass-card aspect-[4/3] flex flex-col justify-end p-6 cursor-pointer border border-slate-200"
              >
                {/* Background Image */}
                <img
                  src={item.image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 opacity-90"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />

                {/* Text Content */}
                <div className="relative z-10">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-purple-300 mb-1 block">
                    {item.category}
                  </span>
                  <h4 className="text-lg font-bold font-display text-white group-hover:text-purple-200 transition-colors flex items-center justify-between">
                    <span>{item.title}</span>
                    <ExternalLink size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h4>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;
